import React, { useState } from 'react';
import WorkspaceShell from './WorkspaceShell';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import {
  useGetDocumentTemplatesQuery,
  useGetWorkspaceDocumentsQuery,
  useCreateWorkspaceDocumentMutation,
  useSignWorkspaceDocumentMutation,
} from '../../features/api/apiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCustomerId } from '../../features/auth/authSlice';

const DocumentsPage = () => {
  const user = useSelector(selectCurrentUser);
  const myId = useSelector(selectCustomerId);
  const { data: templates = [] } = useGetDocumentTemplatesQuery();
  const { data, isLoading, refetch } = useGetWorkspaceDocumentsQuery();
  const [createDoc, { isLoading: creating }] = useCreateWorkspaceDocumentMutation();
  const [signDoc] = useSignWorkspaceDocumentMutation();

  const [form, setForm] = useState({
    title: '',
    doc_type: 'custom',
    body_text: '',
    signer_name: '',
    signer_email: '',
    role_label: 'counterparty',
  });
  const [signature, setSignature] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const created = data?.created || [];
  const toSign = data?.to_sign || [];

  const applyTemplate = (t) => {
    setForm((f) => ({
      ...f,
      title: t.title,
      doc_type: t.doc_type,
      body_text: t.body_text,
    }));
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      await createDoc({
        title: form.title,
        doc_type: form.doc_type,
        body_text: form.body_text,
        signers: [
          {
            signer_name: form.signer_name,
            signer_email: form.signer_email || null,
            role_label: form.role_label,
          },
        ],
      }).unwrap();
      setMsg('Document created and sent for signature.');
      setForm((f) => ({ ...f, title: '', body_text: '', signer_name: '', signer_email: '' }));
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Failed to create document');
    }
  };

  const onSign = async (docId) => {
    if (!signature.trim()) {
      setErr('Type your full legal name as signature.');
      return;
    }
    try {
      await signDoc({ id: docId, signature: signature.trim(), signature_method: 'typed' }).unwrap();
      setMsg('Document signed.');
      setSignature('');
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Sign failed');
    }
  };

  const needsMySig = (doc) =>
    (doc.signers || []).some(
      (s) =>
        s.status === 'pending' &&
        (String(s.signer_id) === String(myId) ||
          (s.signer_email && user?.email && s.signer_email.toLowerCase() === user.email.toLowerCase()))
    );

  return (
    <WorkspaceShell
      title="Digital Document Signing"
      subtitle="Create standard agreements, viewing forms, or intent-to-purchase letters and collect typed e-signatures."
    >
      {msg && <p className="text-success text-sm mb-3">{msg}</p>}
      {err && <p className="text-error text-sm mb-3">{err}</p>}

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h2 className="font-semibold text-lg">Create document</h2>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button
                key={t.doc_type + t.title}
                type="button"
                onClick={() => applyTemplate(t)}
                className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
              >
                {t.title}
              </button>
            ))}
          </div>
          <form onSubmit={onCreate} className="space-y-3">
            <Input label="Title" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Select
              label="Type"
              options={[
                { value: 'custom', label: 'Custom' },
                { value: 'viewing_form', label: 'Viewing form' },
                { value: 'intent_to_purchase', label: 'Intent to purchase' },
                { value: 'nda', label: 'NDA' },
                { value: 'co_broker', label: 'Co-broker addendum' },
              ]}
              value={form.doc_type}
              onChange={(v) => setForm({ ...form, doc_type: v })}
            />
            <textarea
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[160px] font-mono"
              required
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              placeholder="Agreement body…"
            />
            <p className="text-xs text-muted-foreground">You sign automatically as creator. Add one counterparty:</p>
            <Input label="Signer name" required value={form.signer_name}
              onChange={(e) => setForm({ ...form, signer_name: e.target.value })} />
            <Input label="Signer email" type="email" value={form.signer_email}
              onChange={(e) => setForm({ ...form, signer_email: e.target.value })} />
            <Button type="submit" loading={creating} disabled={creating}>Send for signature</Button>
          </form>
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="font-semibold text-lg mb-2">Awaiting your signature</h2>
            {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {[...toSign, ...created.filter(needsMySig)].length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing pending.</p>
            )}
            {[...toSign, ...created.filter(needsMySig)].map((doc) => (
              <div key={`sign-${doc.id}`} className="rounded-xl border border-border bg-card p-4 mb-2">
                <p className="font-medium">{doc.title}</p>
                <p className="text-xs text-muted-foreground capitalize">{doc.doc_type} · {doc.status}</p>
                <pre className="text-xs mt-2 max-h-24 overflow-auto whitespace-pre-wrap bg-muted/40 p-2 rounded">
                  {doc.body_text?.slice(0, 400)}
                  {(doc.body_text?.length || 0) > 400 ? '…' : ''}
                </pre>
                <Input
                  className="mt-2"
                  label="Type full legal name to sign"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                />
                <Button size="sm" className="mt-2" onClick={() => onSign(doc.id)}>Sign document</Button>
              </div>
            ))}
          </div>

          <div>
            <h2 className="font-semibold text-lg mb-2">Documents you created</h2>
            {created.length === 0 && <p className="text-sm text-muted-foreground">None yet.</p>}
            {created.map((doc) => (
              <div key={doc.id} className="rounded-xl border border-border bg-card p-4 mb-2">
                <div className="flex justify-between gap-2">
                  <p className="font-medium">{doc.title}</p>
                  <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize h-fit">{doc.status?.replace('_', ' ')}</span>
                </div>
                <ul className="text-xs mt-2 space-y-1">
                  {(doc.signers || []).map((s) => (
                    <li key={s.id}>
                      {s.signer_name} ({s.role_label || 'signer'}): <strong>{s.status}</strong>
                      {s.signed_at ? ` · ${new Date(s.signed_at).toLocaleString()}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </WorkspaceShell>
  );
};

export default DocumentsPage;
